import { useContext } from 'react';
import { SmartContext } from '../../../../library/Core/SmartContext';

const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
};

const formatDateTime = (value?: string) => {
    if (!value) return '-';
    try {
        return new Intl.DateTimeFormat('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(new Date(value));
    } catch (error) {
        return value;
    }
};

const getStatusBadgeClass = (status?: string) => {
    switch ((status || '').toUpperCase()) {
        case 'NEW':
            return 'bg-primary-subtle text-primary';
        case 'VIEWED':
            return 'bg-info-subtle text-info';
        case 'RESPONDED':
            return 'bg-success-subtle text-success';
        case 'CLOSED':
            return 'bg-secondary-subtle text-secondary';
        default:
            return 'bg-secondary-subtle text-secondary';
    }
};

const getOutcomeBadgeClass = (outcome?: string) => {
    switch ((outcome || '').toUpperCase()) {
        case 'CONVERTED':
            return 'bg-success-subtle text-success';
        case 'NOT_TAKEN_UP':
            return 'bg-secondary-subtle text-secondary';
        case 'DECLINED':
            return 'bg-danger-subtle text-danger';
        case 'PENDING':
        default:
            return 'bg-warning-subtle text-warning';
    }
};

const formatOutcome = (outcome?: string) => {
    if (!outcome) return 'PENDING';
    return outcome.replace(/_/g, ' ');
};

interface MyEnquiriesTableProps {
    onViewInquiry: (inquiry: any) => void;
}

const MyEnquiriesTable = ({ onViewInquiry }: MyEnquiriesTableProps) => {
    const { state } = useContext(SmartContext);
    const inquiryList = state.data?.inquiryList || [];
    const pagination = state.data?.pagination || { total: 0 };
    const resultsSection: any = state.formConfig?.sectionRepository?.find((section: any) => section.id === 'results');
    const columns = resultsSection?.controlGroup?.[0]?.props?.columns || [];

    const renderCell = (row: any, accessorKey: string) => {
        switch (accessorKey) {
            case 'provider':
                return (
                    <div className="d-flex flex-column">
                        <span className="font-16 font-500 text-dark">{row.providerName || row.businessName || '-'}</span>
                        <span className="font-13 text-muted">{row.providerEmail || '-'}</span>
                    </div>
                );
            case 'subject':
                return <span className="font-14 font-500 text-dark">{row.subject || '-'}</span>;
            case 'date':
                return <span className="font-14">{formatDateTime(row.createdAt)}</span>;
            case 'status':
                return <span className={`badge rounded-pill px-3 py-2 font-13 ${getStatusBadgeClass(row.status)}`}>{row.status || 'UNKNOWN'}</span>;
            case 'outcome':
                return <span className={`badge rounded-pill px-3 py-2 font-13 ${getOutcomeBadgeClass(row.outcome)}`}>{formatOutcome(row.outcome)}</span>;
            case 'lastMessage':
                return <span className="font-14 text-muted">{row.lastMessage ? stripHtml(row.lastMessage) : 'No messages yet'}</span>;
            case 'action':
                return (
                    <button type="button" className="btn btn-skyblue btn-sm px-3" onClick={() => onViewInquiry(row)}>
                        View
                    </button>
                );
            default:
                return <span>{row[accessorKey] || '-'}</span>;
        }
    };

    if (inquiryList.length === 0) {
        return (
            <div className="text-center p-5 bg-white border rounded-3">
                <i className="icon-inbox font-48 text-muted d-block mb-3"></i>
                <h5 className="font-16 font-600">No enquiries found</h5>
                <p className="text-muted mb-0">Your sent enquiries to service providers will appear here.</p>
            </div>
        );
    }

    return (
        <div className="results-section">
            <div className="results-header d-flex justify-content-between align-items-center mb-3 gap-3">
                <div className="results-count">
                    <span className="text-muted">Showing {inquiryList.length} of {pagination.total} enquiries</span>
                </div>
            </div>

            <div className="table-responsive attendee_dtl bg-white rounded-3">
                <table className="table mb-0" style={{ minWidth: '980px' }}>
                    <thead>
                        <tr>
                            {columns.map((column: any) => (
                                <th key={column.accessorKey}>{column.header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {inquiryList.map((row: any) => (
                            <tr key={row.id}>
                                {columns.map((column: any) => (
                                    <td key={`${row.id}-${column.accessorKey}`} className="align-middle">
                                        {renderCell(row, column.accessorKey)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MyEnquiriesTable;